import { FC, UIEvent, useCallback, useEffect, useState } from 'react';

import { Button } from 'components';
import { If } from 'components/Generics';
import ResponsiveModal from 'components/Modals/ResponsiveModal/ResponsiveModal';
import { useGetSalesforceEmailTemplates } from 'services/hooks/SalesforceAccounts.hooks';
import { SALESFORCE_EMAIL_TEMPLATES_PAGE_SIZE } from 'services/query/SalesforceLeads.queries';
import { SalesforceEmailTemplate } from 'types/SalesforceAccounts.types';

const getTemplateLabel = (template: SalesforceEmailTemplate): string =>
  template.name || template.developerName || template.id;

interface Props {
  open: boolean;
  relatedEntityType: string;
  onClose: () => void;
  onSelect: (template: SalesforceEmailTemplate) => void;
}

const SalesforceEmailTemplatePickerModal: FC<Props> = ({
  open,
  relatedEntityType,
  onClose,
  onSelect
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [templates, setTemplates] = useState<SalesforceEmailTemplate[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [fetchTemplates, { loading }] = useGetSalesforceEmailTemplates({
    fetchPolicy: 'network-only'
  });

  const loadTemplates = useCallback(
    (search: string, offset: number, append: boolean) => {
      fetchTemplates({
        variables: {
          filter: {
            search: search.trim() || undefined,
            relatedEntityType,
            isActive: true,
            limit: SALESFORCE_EMAIL_TEMPLATES_PAGE_SIZE,
            offset
          }
        }
      }).then(({ data }) => {
        const results = data?.getSalesforceEmailTemplates ?? [];
        setTemplates((prev) => (append ? [...prev, ...results] : results));
        setHasMore(results.length >= SALESFORCE_EMAIL_TEMPLATES_PAGE_SIZE);
      });
    },
    [fetchTemplates, relatedEntityType]
  );

  useEffect(() => {
    if (!open) return;

    setSearchInput('');
    setAppliedSearch('');
    setTemplates([]);
    setHasMore(false);
    setHasSearched(true);
    loadTemplates('', 0, false);
  }, [open, loadTemplates]);

  const handleSearch = () => {
    setAppliedSearch(searchInput);
    setHasSearched(true);
    setTemplates([]);
    setHasMore(false);
    loadTemplates(searchInput, 0, false);
  };

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const nearBottom = scrollHeight - scrollTop <= clientHeight + 40;

    if (nearBottom && hasMore && !loading) {
      loadTemplates(appliedSearch, templates.length, true);
    }
  };

  const handleSelect = (template: SalesforceEmailTemplate) => {
    onSelect(template);
    onClose();
  };

  return (
    <ResponsiveModal
      isOpen={open}
      onClose={onClose}
      title='Select Email Template'
      showHeader
      className='md:w-[560px] md:!h-[520px] flex flex-col overflow-hidden'
      customModalClassNames={{ modal: 'z-[1100]', overlay: 'z-[1090]' }}
    >
      <div className='flex flex-1 min-h-0 flex-col gap-4'>
        <div className='flex gap-2 shrink-0'>
          <input
            type='text'
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleSearch();
              }
            }}
            placeholder='Search by name or developer name'
            className='flex-1 border border-LIGHT_GRAY rounded-lg px-3 py-2.5 text-sm text-DEFAULT_TEXT focus:outline-none focus:border-PRIMARY'
          />
          <Button
            type='button'
            className='bg-PRIMARY text-white text-center py-2.5 px-4 text-sm whitespace-nowrap disabled:opacity-50'
            onClick={handleSearch}
            disabled={loading && templates.length === 0}
          >
            Search
          </Button>
        </div>

        <div
          className='flex-1 min-h-0 overflow-y-auto border border-LIGHT_GRAY rounded-lg'
          onScroll={handleScroll}
        >
          <If condition={loading && templates.length === 0}>
            <div className='flex h-full items-center justify-center'>
              <p className='text-xs text-DISABLED'>Loading templates...</p>
            </div>
          </If>

          <If condition={hasSearched && !loading && templates.length === 0}>
            <div className='flex h-full items-center justify-center'>
              <p className='text-sm text-DISABLED'>No templates found.</p>
            </div>
          </If>

          <If condition={templates.length > 0}>
            <ul className='divide-y divide-LIGHT_GRAY'>
              {templates.map((template) => (
                <li key={template.id}>
                  <button
                    type='button'
                    onClick={() => handleSelect(template)}
                    className='w-full text-left px-4 py-3 hover:bg-GRAY5 transition-colors'
                  >
                    <p className='text-sm font-medium text-DEFAULT_TEXT'>
                      {getTemplateLabel(template)}
                    </p>
                    <If condition={!!template.subject}>
                      <p className='text-xs text-DISABLED mt-0.5 truncate'>
                        {template.subject}
                      </p>
                    </If>
                    <If condition={!!template.description}>
                      <p className='text-xs text-DISABLED mt-0.5 line-clamp-2'>
                        {template.description}
                      </p>
                    </If>
                  </button>
                </li>
              ))}
            </ul>
          </If>

          <If condition={loading && templates.length > 0}>
            <p className='py-3 text-center text-xs text-DISABLED'>
              Loading more...
            </p>
          </If>
        </div>

        <div className='flex shrink-0 justify-end pt-2'>
          <Button
            type='button'
            className='text-DEFAULT_TEXT text-center text-sm'
            onClick={onClose}
          >
            Cancel
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
};

export default SalesforceEmailTemplatePickerModal;
